import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { IMAGES } from "@/data/content";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, reduce ? 1 : 0.2]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-32">
        <motion.img
          src={IMAGES.hero}
          alt="Dubai skyline — Burj Al Arab and Burj Khalifa"
          className="w-full h-full object-cover object-center"
          initial={{ scale: reduce ? 1 : 1.18, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-[#0A1128]/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/45 to-[#0A1128]/15" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128]/55 via-transparent to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 pb-44 md:pb-24 pt-48 w-full">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.9 }}
          className="text-xs uppercase tracking-[0.35em] text-[#d4af37] font-medium mb-8"
          data-testid="hero-overline"
        >
          RK Tourism & Travels — United Arab Emirates
        </motion.p>

        <h1 className="font-hero font-light tracking-tight leading-[0.95] text-[15vw] sm:text-7xl lg:text-8xl xl:text-[7.5rem] text-slate-50">
          <MaskedLine delay={0.35}>Curating</MaskedLine>
          <MaskedLine delay={0.5}>
            <span className="italic text-[#d4af37]">Exceptional</span>
          </MaskedLine>
          <MaskedLine delay={0.65}>Journeys.</MaskedLine>
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9 }}
            className="max-w-md text-slate-300 text-base md:text-lg leading-relaxed"
            data-testid="hero-subtext"
          >
            From the skyline of Dubai to the streets of Singapore — bespoke tours,
            seamless visit visas and journeys designed entirely around you.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9 }}
            className="flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => scrollToSection("#booking")}
              className="group flex items-center gap-3 bg-[#d4af37] text-[#0A1128] px-8 py-4 text-sm tracking-[0.12em] uppercase font-medium transition-colors duration-300 hover:bg-[#f3e5ab]"
            >
              Book Now
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToSection("#destinations")}
              className="border border-slate-50/40 bg-[#0A1128]/30 backdrop-blur-sm text-slate-50 px-8 py-4 text-sm tracking-[0.12em] uppercase transition-colors duration-300 hover:border-[#d4af37] hover:text-[#d4af37]"
            >
              Explore Destinations
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden md:flex flex-col items-center gap-3 text-slate-300"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase rotate-90 origin-center translate-y-[-8px]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
}
